export interface AdminOperator {
  uid: string;
  displayName: string | null;
  email: string | null;
  photoURL: string | null;
  providerId: 'password';
  isAnonymous: false;
}

export interface AdminLoginResult {
  accessToken: string;
  expiresAt: string;
  firebaseToken: string;
  operator: { username: string };
}

export interface AdminSessionInfo {
  operator: { username: string };
  projectId: string;
  environment: string;
  expiresAt: string;
}

export interface AdminAiInsightResult {
  news: string[];
  positive: string[];
  negative: string[];
  recommendation: string;
}

export type AdminSystemResource =
  'ai-insight' | 'whatnews-zma' | 'whatnews-webapp';

export type AdminWhatNewsResource = Exclude<AdminSystemResource, 'ai-insight'>;

export class AdminApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string | null = null,
    public readonly details: readonly string[] = [],
  ) {
    super(message);
    this.name = 'AdminApiError';
  }
}

interface AdminApiErrorPayload {
  error?: unknown;
  code?: unknown;
  message?: unknown;
}

const MAX_ERROR_DETAIL_LENGTH = 500;

function safeErrorText(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const text = value.trim();
  if (!text || text.length > MAX_ERROR_DETAIL_LENGTH) return null;
  return text;
}

async function readAdminApiError(response: Response): Promise<{
  code: string | null;
  details: string[];
}> {
  const contentType = response.headers.get('Content-Type') ?? '';
  if (!contentType.toLowerCase().includes('application/json')) {
    return { code: null, details: [] };
  }

  try {
    const payload = (await response.json()) as AdminApiErrorPayload;
    if (!payload || typeof payload !== 'object') {
      return { code: null, details: [] };
    }

    const rawMessages = Array.isArray(payload.message)
      ? payload.message
      : [payload.message];
    const details = rawMessages
      .map(safeErrorText)
      .filter((detail): detail is string => detail !== null)
      .slice(0, 10);
    const code = safeErrorText(payload.code) ?? safeErrorText(payload.error);

    return { code, details };
  } catch {
    return { code: null, details: [] };
  }
}

export function resolveAdminApiBaseUrl(
  environment: Record<string, unknown> = import.meta.env,
): string {
  const raw = environment.VITE_ADMIN_API_BASE_URL;
  return typeof raw === 'string' ? raw.trim().replace(/\/+$/, '') : '';
}

export function buildAdminApiUrl(
  baseUrl: string,
  routePath: string,
  query: Record<string, string> = {},
): URL {
  const url = new URL(`${baseUrl}${routePath}`);
  for (const [key, value] of Object.entries(query)) {
    url.searchParams.set(key, value);
  }
  return url;
}

interface AdminApiRequestOptions {
  token?: string;
  method?: 'GET' | 'POST';
  body?: unknown;
  query?: Record<string, string>;
  fetchImplementation?: typeof fetch;
  onUnauthorized?: () => void;
}

export async function fetchAdminApiJson<T>(
  baseUrl: string,
  routePath: string,
  {
    token,
    method = 'GET',
    body,
    query,
    fetchImplementation = fetch,
    onUnauthorized,
  }: AdminApiRequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }

  const response = await fetchImplementation(
    buildAdminApiUrl(baseUrl, routePath, query),
    {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      onUnauthorized?.();
    }
    const { code, details } = await readAdminApiError(response);
    throw new AdminApiError(
      details.length > 0
        ? details.join('; ')
        : `Admin API request failed with status ${response.status}`,
      response.status,
      code,
      details,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export async function loginAdmin(
  baseUrl: string,
  username: string,
  password: string,
  options: Pick<AdminApiRequestOptions, 'fetchImplementation'> = {},
): Promise<AdminLoginResult> {
  return fetchAdminApiJson<AdminLoginResult>(baseUrl, '/admin/login', {
    ...options,
    method: 'POST',
    body: { username, password },
  });
}

export async function fetchAdminSession(
  baseUrl: string,
  token: string,
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<AdminSessionInfo> {
  return fetchAdminApiJson<AdminSessionInfo>(baseUrl, '/admin/session', {
    ...options,
    token,
  });
}

export async function logoutAdmin(
  baseUrl: string,
  token: string,
  options: Pick<AdminApiRequestOptions, 'fetchImplementation'> = {},
): Promise<void> {
  await fetchAdminApiJson<{ acknowledged: true }>(baseUrl, '/admin/logout', {
    ...options,
    method: 'POST',
    token,
  });
}

/**
 * Starts one paid backend generation and replaces the live AI Insight when it
 * succeeds. The caller owns confirmation; the backend owns AI access and the
 * Firebase write.
 */
export async function rerunAdminAiInsight(
  baseUrl: string,
  token: string,
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<AdminAiInsightResult> {
  return fetchAdminApiJson<AdminAiInsightResult>(
    baseUrl,
    '/admin/ai-insight/rerun',
    {
      ...options,
      method: 'POST',
      token,
    },
  );
}

export interface LedgerBookListPage {
  uids: string[];
  nextStartAfter: string | null;
  total: number;
}

/**
 * One page of the sổ vàng directory, as identifiers.
 *
 * The listing is server-side because `/users` is not readable by this browser
 * and must never become readable: a Realtime Database read grant cascades, so
 * opening the parent would expose every PIN hash and notification token beneath
 * it. The backend returns keys only; the branches themselves are then read
 * directly with the read-only claim, which the rules already scope to the seven
 * granted branches.
 */
export async function fetchLedgerBookList(
  baseUrl: string,
  token: string,
  { limit, startAfter }: { limit?: number; startAfter?: string } = {},
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<LedgerBookListPage> {
  const query: Record<string, string> = {};
  if (limit !== undefined) query.limit = String(limit);
  if (startAfter !== undefined) query.startAfter = startAfter;

  return fetchAdminApiJson<LedgerBookListPage>(
    baseUrl,
    '/admin/ledger-book/list',
    { ...options, token, query },
  );
}

/**
 * `/system` content is read straight from the database by the delegate, which
 * the rules already permit for any signed-in client — so there is no read
 * endpoint for it here, only this write.
 */
export async function updateAdminSystemResource<T>(
  baseUrl: string,
  token: string,
  resource: AdminSystemResource,
  values: unknown,
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > & { entityId?: string } = {},
): Promise<{ resource: string; entityId?: string; values: T }> {
  const { entityId, ...requestOptions } = options;

  return fetchAdminApiJson<{
    resource: string;
    entityId?: string;
    values: T;
  }>(baseUrl, '/admin/system/update', {
    ...requestOptions,
    token,
    method: 'POST',
    // `entityId` identifies an existing What's New entry. Omitting it asks
    // the backend to append a new entry while preserving the array root.
    body: { resource, values, ...(entityId ? { entityId } : {}) },
  });
}

export async function deleteAdminWhatNewsEntry(
  baseUrl: string,
  token: string,
  resource: AdminWhatNewsResource,
  entityId: string,
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<{ resource: AdminWhatNewsResource; entityId: string }> {
  return fetchAdminApiJson(baseUrl, '/admin/system/delete', {
    ...options,
    token,
    method: 'POST',
    body: { resource, entityId },
  });
}

/**
 * Edits one record inside a sổ's branch.
 *
 * A branch KEY, never a raw path. The backend re-checks the customer contract
 * (productKey exists, quantity >= 0.001, unitPrice >= 1000) and refuses a change
 * to a field a recorded sale was computed from.
 */
export async function updateLedgerBookRecord(
  baseUrl: string,
  token: string,
  payload: {
    uid: string;
    branch: string;
    entityId?: string;
    values: unknown;
  },
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<{ uid: string; branch: string; values: unknown }> {
  return fetchAdminApiJson(baseUrl, '/admin/ledger-book/update', {
    ...options,
    token,
    method: 'POST',
    body: payload,
  });
}

/**
 * Deletes one record inside a sổ. The backend removes its Storage attachments
 * first — `fullpath` is the only pointer to a blob — and, for a tin đăng, its
 * public `/market/posts` copy.
 */
export async function deleteLedgerBookRecord(
  baseUrl: string,
  token: string,
  payload: { uid: string; branch: string; entityId: string },
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<unknown> {
  return fetchAdminApiJson(baseUrl, '/admin/ledger-book/record/delete', {
    ...options,
    token,
    method: 'POST',
    body: payload,
  });
}

/**
 * Erases a whole sổ: every branch, its marketplace copies, and every attachment
 * in Storage. There is no undo.
 */
export async function purgeLedgerBook(
  baseUrl: string,
  token: string,
  uid: string,
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<unknown> {
  return fetchAdminApiJson(baseUrl, '/admin/ledger-book/purge', {
    ...options,
    token,
    method: 'POST',
    body: { uid },
  });
}

export async function updateAdminRealtimePrice(
  baseUrl: string,
  token: string,
  key: string,
  values: Record<string, unknown>,
  options: Pick<
    AdminApiRequestOptions,
    'fetchImplementation' | 'onUnauthorized'
  > = {},
): Promise<{ key: string; values: Record<string, unknown> }> {
  return fetchAdminApiJson(baseUrl, '/admin/price/update', {
    ...options,
    token,
    method: 'POST',
    body: { key, values },
  });
}
